import React, { useMemo, useState, useEffect, useRef } from 'react';
import { GameContentProps } from './GameContent.types';
import { ContentContainer, ContentWrapper } from './GameContent.styles';
import { parseBBCode } from '../bbcode/bbcode.parser';
import { BBCodeRenderer } from '../bbcode/bbcode.renderer';
import { resolveImagePath } from '../utils/imageResolver';
import {
  sanMartinContentEN,
  sanMartinContentES,
  catervaContentEN,
  catervaContentES,
} from '../utils/contentLoader';

interface ContentMap {
  [gameId: string]: {
    en: string;
    es: string;
  };
}

const GAME_CONTENT_MAP: ContentMap = {
  sanmartin: {
    en: sanMartinContentEN,
    es: sanMartinContentES,
  },
  caterva: {
    en: catervaContentEN,
    es: catervaContentES,
  },
};

type AnimationPhase = 'entering' | 'visible' | 'exiting';

export const GameContentSection: React.FC<GameContentProps> = ({ gameId, language }) => {
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>('entering');
  const [displayedGameId, setDisplayedGameId] = useState(gameId);
  const [displayedLanguage, setDisplayedLanguage] = useState(language);
  const isFirstRenderRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      setAnimationPhase('visible');
      return;
    }

    if (gameId !== displayedGameId || language !== displayedLanguage) {
      setAnimationPhase('exiting');

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setDisplayedGameId(gameId);
        setDisplayedLanguage(language);
        setAnimationPhase('entering');

        timeoutRef.current = setTimeout(() => {
          setAnimationPhase('visible');
        }, 50);
      }, 500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [gameId, language, displayedGameId, displayedLanguage]);

  const rawContent = useMemo(() => {
    const gameContent = GAME_CONTENT_MAP[displayedGameId];
    if (!gameContent) return '';
    return gameContent[displayedLanguage] || '';
  }, [displayedGameId, displayedLanguage]);

  const parsedContent = useMemo(() => {
    if (!rawContent) {
      return { ast: { type: 'document' as const, children: [] }, errors: [] };
    }
    return parseBBCode(rawContent);
  }, [rawContent]);

  const imageResolver = useMemo(() => {
    return (src: string) => resolveImagePath(src, displayedGameId);
  }, [displayedGameId]);

  if (!rawContent) {
    return null;
  }

  return (
    <ContentContainer
      $animationPhase={animationPhase}
      key={`${displayedGameId}-${displayedLanguage}`}
    >
      <ContentWrapper>
        <BBCodeRenderer ast={parsedContent.ast} resolveImage={imageResolver} />
      </ContentWrapper>
    </ContentContainer>
  );
};

GameContentSection.displayName = 'GameContentSection';

export function setGameContent(gameId: string, language: 'en' | 'es', content: string): void {
  if (!GAME_CONTENT_MAP[gameId]) {
    GAME_CONTENT_MAP[gameId] = { en: '', es: '' };
  }
  GAME_CONTENT_MAP[gameId][language] = content;
}
